import React, { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Menu from "@mui/material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import PasswordLessAuthService from "../../core/service/passwordless-service";
import { FHIR_ADMIN_PROVIDER_ID } from "../../core/service/provider-token-service";
import dearLogo from "../../assets/images/dear_logo.png";

// Each console is its own workspace: "provider" (manage a real provider) vs "fhir" (the FHIR
// admin console, entered via the reserved "dearhealth" provider). Links are scoped to the
// active workspace so neither console exposes the other's dead-end destination.
type Workspace = "provider" | "fhir";
type NavLink = { label: string; path: string; workspace: Workspace };
const navLinks: NavLink[] = [
  { label: "Overview", path: "/admin/overview", workspace: "provider" },
  { label: "FHIR Server", path: "/admin/fhir-server", workspace: "fhir" },
];
const passwordLessAuthService = new PasswordLessAuthService();

const DhAppbar = () => {
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Nav visibility is driven by the minted token + chosen workspace. Before a workspace is
  // chosen (e.g. on the provider-selection screen) no adminToken exists, so no links show and
  // nothing can 401. useLocation re-renders on navigation, so links appear once a token exists.
  const hasAdminToken = Boolean(sessionStorage.getItem("adminToken"));
  const providerId = sessionStorage.getItem("providerId");
  const providerName = sessionStorage.getItem("providerName") || providerId;
  const workspace: Workspace = providerId === FHIR_ADMIN_PROVIDER_ID ? "fhir" : "provider";
  // The provider-selection screen comes before committing to a workspace (and any token/provider
  // still in storage there is stale from a prior session), so it shows no workspace nav or chip.
  const onProviderSelection = location.pathname === "/admin/providers";
  const visibleLinks =
    hasAdminToken && !onProviderSelection
      ? navLinks.filter((link) => link.workspace === workspace)
      : [];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    passwordLessAuthService.signOut();
    navigate("/");
  };

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{ borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ gap: 1.5, minHeight: { xs: 60, md: 66 } }}>
          <ButtonBase
            onClick={() => navigate("/console")}
            aria-label="Go to console"
            sx={{ borderRadius: 1.5, p: 0.5 }}
          >
            <Box
              component="img"
              src={dearLogo}
              alt="DEARhealth"
              sx={{ height: 30, display: "block" }}
            />
          </ButtonBase>

          <Box sx={{ flexGrow: 1, display: "flex", gap: 0.5, ml: 2 }}>
            {visibleLinks.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  disableRipple
                  sx={{
                    px: 1.75,
                    color: active ? "primary.main" : "text.secondary",
                    bgcolor: (t) =>
                      active ? alpha(t.palette.primary.main, 0.1) : "transparent",
                    "&:hover": {
                      bgcolor: (t) =>
                        active ? alpha(t.palette.primary.main, 0.14) : "action.hover",
                      color: active ? "primary.main" : "text.primary",
                    },
                  }}
                >
                  {link.label}
                </Button>
              );
            })}
          </Box>

          <Stack direction="row" spacing={1.25} alignItems="center">
            {hasAdminToken && providerName && !onProviderSelection && (
              <Chip
                size="small"
                label={providerName}
                variant="outlined"
                sx={{ display: { xs: "none", sm: "flex" }, maxWidth: 220 }}
              />
            )}
            <Tooltip title="Account">
              <IconButton
                onClick={handleOpenUserMenu}
                size="small"
                sx={{ p: 0.25 }}
                aria-label="Account menu"
                aria-haspopup="true"
                aria-expanded={Boolean(anchorElUser)}
                aria-controls={anchorElUser ? "menu-appbar" : undefined}
              >
                <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main" }}>
                  <PersonRoundedIcon fontSize="small" />
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: 1 }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              PaperProps={{ sx: { minWidth: 180, borderRadius: 2 } }}
            >
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
export default DhAppbar;
